import classNames from 'classnames';

import { useUiMessages } from '../../messages';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { Typography } from '../Typography';

import styles from './Pagination.module.css';
import { getPaginationRange } from './paginationRange';

export type PaginationType = 'numbers' | 'page-info' | 'prev-next';
export type PaginationShape = 'square' | 'circle';

export interface PaginationLabels {
  /** nav landmark name. */
  root: string;
  previous: string;
  next: string;
  /** Builds the "Page X of Y" text (page-info type). */
  pageInfo: (page: number, total: number) => string;
  /** aria-label for a page button. */
  goToPage: (page: number) => string;
}

const DEFAULT_LABELS: PaginationLabels = {
  root: 'Pagination',
  previous: 'Previous',
  next: 'Next',
  pageInfo: (page, total) => `Page ${String(page)} of ${String(total)}`,
  goToPage: (page) => `Go to page ${String(page)}`,
};

export interface PaginationProps {
  /** Current page (1-based). */
  page: number;
  /** Total number of pages. */
  total: number;
  onPageChange: (page: number) => void;
  /**
   * Numbered page buttons, a compact "Page X of Y" readout, or Previous/Next alone (`prev-next`,
   * the phone layout — the position is then announced to assistive technology only).
   */
  type?: PaginationType;
  /** Page-button shape (numbers type). */
  shape?: PaginationShape;
  siblingCount?: number;
  boundaryCount?: number;
  /** Show text on the Previous/Next buttons; false renders icon-only arrows. */
  showPreviousNextLabels?: boolean;
  labels?: Partial<PaginationLabels>;
  className?: string;
}

/**
 * Page navigation following the WAI-ARIA pattern: a `nav` landmark wrapping Previous/Next controls
 * and, for the `numbers` type, a list of page buttons with `aria-current="page"` on the active one
 * and `…` for truncated runs. The `page-info` type swaps the number list for a "Page X of Y"
 * readout (the Figma mobile layout), and `prev-next` keeps only the two buttons, with the same
 * readout visually hidden so a screen reader still hears where it is. Previous/Next disable at
 * the bounds.
 *
 * @interim Seeded from the Figma "Pagination"; replaceable by the UI Kit without API breakage.
 */
export function Pagination({
  page,
  total,
  onPageChange,
  type = 'numbers',
  shape = 'square',
  siblingCount = 1,
  boundaryCount = 1,
  showPreviousNextLabels = true,
  labels,
  className,
}: PaginationProps) {
  const messages = useUiMessages();
  const t = { ...DEFAULT_LABELS, ...messages.pagination, ...labels };
  const atStart = page <= 1;
  const atEnd = page >= total;

  const goTo = (next: number) => {
    const clamped = Math.min(Math.max(next, 1), total);
    if (clamped !== page) onPageChange(clamped);
  };

  const prevNext = (direction: 'previous' | 'next') => {
    const isPrev = direction === 'previous';
    const label = isPrev ? t.previous : t.next;
    const arrow = <Icon name={isPrev ? 'arrow-left' : 'arrow-right'} size={20} />;
    const iconOnly = !showPreviousNextLabels;
    // Button's icon-only variant renders only `iconLeading`, so the arrow goes there when the
    // label is hidden; with a label, Next keeps its arrow trailing.
    const iconProps = iconOnly || isPrev ? { iconLeading: arrow } : { iconTrailing: arrow };
    return (
      <Button
        hierarchy="secondary"
        size="md"
        iconOnly={iconOnly}
        aria-label={label}
        disabled={isPrev ? atStart : atEnd}
        {...iconProps}
        onClick={() => {
          goTo(isPrev ? page - 1 : page + 1);
        }}
      >
        {label}
      </Button>
    );
  };

  return (
    <nav className={classNames(styles.root, className)} aria-label={t.root}>
      <div className={styles.side}>{prevNext('previous')}</div>

      {type === 'numbers' ? (
        <ul className={styles.numbers}>
          {getPaginationRange(page, total, siblingCount, boundaryCount).map((item, index) =>
            item === 'ellipsis' ? (
              <li key={`ellipsis-${String(index)}`} aria-hidden="true" className={styles.ellipsis}>
                <Typography as="span" variant="text-sm" weight="medium">
                  …
                </Typography>
              </li>
            ) : (
              <li key={item}>
                <button
                  type="button"
                  className={classNames(
                    styles.page,
                    styles[shape],
                    item === page && styles.pageCurrent,
                  )}
                  aria-current={item === page ? 'page' : undefined}
                  aria-label={t.goToPage(item)}
                  onClick={() => {
                    goTo(item);
                  }}
                >
                  <Typography as="span" variant="text-sm" weight="medium">
                    {item}
                  </Typography>
                </button>
              </li>
            ),
          )}
        </ul>
      ) : (
        <Typography
          as="span"
          variant="text-sm"
          weight="medium"
          className={classNames(type === 'prev-next' ? styles.srOnly : styles.info)}
        >
          {t.pageInfo(page, total)}
        </Typography>
      )}

      <div className={classNames(styles.side, styles.sideEnd)}>{prevNext('next')}</div>
    </nav>
  );
}
